import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

const LightboxContext = createContext(null);

export const useLightbox = () => {
  const ctx = useContext(LightboxContext);
  if (!ctx) {
    throw new Error("useLightbox must be used inside LightboxProvider");
  }
  return ctx;
};

export function LightboxProvider({ children }) {
  const [item, setItem] = useState(null);

  const open = useCallback((src, alt = "") => setItem({ src, alt }), []);
  const close = useCallback(() => setItem(null), []);

  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [item, close]);

  return (
    <LightboxContext.Provider value={{ open, close }}>
      {children}
      <AnimatePresence>
        {item && (
          <motion.div
            key="lb-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={close}
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
            data-testid="lightbox-backdrop"
          >
            <button
              onClick={close}
              data-testid="lightbox-close"
              aria-label="Close"
              className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 border border-white/40 bg-black/40 text-white flex items-center justify-center hover:bg-white hover:text-black transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <motion.img
              key="lb-img"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.97, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.65, 0.05, 0.3, 1] }}
              src={item.src}
              alt={item.alt}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-full object-contain shadow-[0_20px_60px_-10px_rgba(0,0,0,0.6)] cursor-default"
              data-testid="lightbox-image"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </LightboxContext.Provider>
  );
}
